import { z } from "zod";
import { localToIso } from "@/utils/appointments.utils";
import { DEFAULT_COUNTRY, isValidPhone } from "@/lib/phone";
import type { CountryCode } from "libphonenumber-js/max";
import {
  AskCategory,
  AskScope,
  Audience,
  ClientRelease,
  DealAssigneeRole,
  DealHealth,
  DealPhase,
  FinancingContingencyMode,
  LoanType,
  NextStepStatus,
  ParticipantRole,
  PurchaseMethod,
  SnapshotOwner,
  TaskPriority,
} from "@/enum/common.enum";

const optionalEmail = z.union([z.literal(""), z.string().trim().email("Enter a valid email address.")]);
const optionalUrl = z.union([z.literal(""), z.string().trim().url("Enter a valid URL.")]);
const optionalPositiveNumber = z.union([
  z.literal(""),
  z.coerce.number().nonnegative("Enter zero or a positive number."),
]);

export const delegatedInviteSchema = z.object({
  email: z.string().trim().min(1, "Email is required.").email("Enter a valid email address."),
  name: z.string().trim().optional(),
});

export const scheduleTaskSchema = z.object({
  date: z.string().min(1, "Date is required."),
  time: z.string().default(""),
  notes: z.string().trim().max(1_000, "Keep details under 1,000 characters.").default(""),
});

export const askSchema = z.object({
  subject: z.string().trim().min(1, "Subject is required."),
  body: z.string().trim().min(1, "Question is required."),
  category: z.nativeEnum(AskCategory),
  scope: z.nativeEnum(AskScope),
});

export const dealUpdateSchema = z.object({
  subject: z.string().trim().min(1, "Subject is required."),
  body: z.string().trim().min(1, "Write the update you want to send."),
  scope: z.nativeEnum(AskScope),
});

export const clientInviteSchema = z.object({
  email: z.string().trim().min(1, "Email is required.").email("Enter a valid email address."),
  name: z.string().trim().optional(),
});

export const collaboratorInviteSchema = z.object({ lender_email: optionalEmail, attorney_email: optionalEmail });

export const taskSchema = z.object({
  title: z.string().trim().min(1, "Task name is required."),
  assigned_role: z.nativeEnum(DealAssigneeRole, { message: "Select an assignee." }),
  phase: z.nativeEnum(DealPhase, { message: "Select a task type." }),
  due_date: z.string().default(""),
  due_date_source: z.enum(["manual", "relative_to_closing", "template"]).default("manual"),
  due_date_offset_days: z.preprocess(
    (value) => (value === "" || value == null ? null : Number(value)),
    z.number().int().min(0, "Enter zero or more days.").max(365, "Keep the offset under a year.").nullable()
  ).optional(),
  description: z.string().trim().max(2_000, "Keep the description under 2,000 characters.").default(""),
  priority: z.nativeEnum(TaskPriority).default(TaskPriority.MEDIUM),
  // Seeded checklist rows are the client's storyline, so a hand-added row
  // defaults to client-visible too — an agent who wants a private note flips
  // the switch. `after_task_id` is carried through untouched; it tells the
  // backend to drop the row directly beneath an existing one (how a
  // contingency lands in the reserved slot instead of at the bottom).
  audience: z.nativeEnum(Audience).default(Audience.CLIENT),
  client_release: z.nativeEnum(ClientRelease).default(ClientRelease.AUTO),
  after_task_id: z.string().optional(),
  request_type: z.enum(["none", "action", "signature", "document", "info"]).default("none"),
}).superRefine((values, ctx) => {
  if (values.due_date_source === "relative_to_closing" && values.due_date_offset_days == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["due_date_offset_days"],
      message: "Enter days before closing.",
    });
  }
});

export const documentSchema = z.object({
  name: z.string().trim().min(1, "Document name is required."),
  category: z.string().min(1, "Select a category."),
  owner_role: z.nativeEnum(DealAssigneeRole, { message: "Select an owner." }),
  url: optionalUrl,
  storage_path: z.string().nullable(),
  content_type: z.string().nullable(),
  size: z.number().nullable(),
  original_filename: z.string().nullable(),
  audience: z.nativeEnum(Audience, { message: "Choose who can see this." }),
}).refine(({ storage_path, url }) => Boolean(storage_path || url), {
  message: "Upload a file or paste a URL.",
  path: ["url"],
});

export const participantSchema = z.object({
  name: z.string().trim().min(1, "Name is required."),
  email: z.string().trim().min(1, "Email is required.").email("Enter a valid email address."),
  role: z.nativeEnum(ParticipantRole),
  company: z.string().trim().max(120, "Keep the company name under 120 characters.").default(""),
  phone: z.string().trim().optional(),
  /** ISO 3166-1 alpha-2 the phone is read against. Local to the form — the
   *  server is only ever sent E.164. Required rather than `.default()`, which
   *  would make the schema's input type diverge from its output. */
  phone_country: z.string().trim().min(2),
  send_sms: z.boolean().optional(),
})
  // Same validation as the org/deal invite form in `schemas/invites.ts`, and
  // for the same reason: this is the second way into the same SMS invite, so
  // leaving it on a presence check would let a number the backend rejects
  // through one door while the other stopped it.
  .superRefine((values, ctx) => {
    if (!values.send_sms) return;
    if (!values.phone) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["phone"], message: "Add a mobile number to send a text." });
      return;
    }
    if (!isValidPhone(values.phone, (values.phone_country || DEFAULT_COUNTRY) as CountryCode)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phone"],
        message: "That isn't a valid number for the selected country. Check the country code and try again.",
      });
    }
  });

export const micrositeSchema = z.object({
  hero_image_url: z.string(),
  welcome_message: z.string().trim().max(280, "Keep the welcome message under 280 characters."),
  agent_photo_url: z.string(),
  agent_phone: z.string().trim().max(40, "Keep the phone number under 40 characters."),
  agent_tagline: z.string().trim().max(120, "Keep the tagline under 120 characters."),
});

/** The ten terms iteration 46 added. Shared verbatim by the under-contract
 *  prompt and the full deal-summary editor so the two cannot drift. */
const contractPromptFields = {
  earnest_money_holder: z.string().max(200),
  option_fee_amount: optionalPositiveNumber,
  earnest_money_due_date: z.string(),
  option_fee_due_date: z.string(),
  seller_names: z.string().max(300),
  buyer_names: z.string().max(300),
  inspection_deadline_1: z.string(),
  inspection_deadline_2: z.string(),
  inspection_company: z.string().max(200),
  inspection_date: z.string(),
  inspection_notes: z.string().max(2_000, "Keep inspection notes under 2,000 characters."),
  inspection_start_time: z.string(),
  inspection_end_time: z.string(),
  possession_datetime: z.string(),
  utility_connect_date: z.string(),
  utility_disconnect_date: z.string(),
  closing_law_firm: z.string().max(200),
  listing_agent: z.string().max(200),
  listing_brokerage: z.string().max(200),
  coop_brokerage: z.string().max(200),
  coop_agent: z.string().max(200),
  further_considerations: z.string().max(4_000, "Keep the notes under 4,000 characters."),
};

/** Inspection happens inside due diligence, which ends before closing. Each
 *  pair is checked only when both dates are set; the error lands on the later
 *  field, the one an agent most likely mistyped. Dates are YYYY-MM-DD, so a
 *  string compare is a date compare. */
const checkContractDateOrder = (
  values: { inspection_date: string; due_diligence_end_date: string; closing_date: string },
  ctx: z.RefinementCtx,
) => {
  const pairs = [
    ["inspection_date", "due_diligence_end_date", "Due diligence can't end before the inspection."],
    ["due_diligence_end_date", "closing_date", "Closing can't be before due diligence ends."],
  ] as const;
  for (const [earlier, later, message] of pairs) {
    const a = values[earlier]?.slice(0, 10);
    const b = values[later]?.slice(0, 10);
    if (a && b && b < a) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [later], message });
  }
};

/** An arrival window ends after it starts. "HH:MM" compares as a string. */
const checkInspectionWindow = (
  values: { inspection_start_time: string; inspection_end_time: string },
  ctx: z.RefinementCtx,
) => {
  const { inspection_start_time: start, inspection_end_time: end } = values;
  if (start && end && end <= start) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["inspection_end_time"], message: "The window has to end after it starts." });
  }
};

export const dealSummarySchema = z.object({
  purchase_price: optionalPositiveNumber,
  closing_date: z.string(),
  due_diligence_end_date: z.string(),
  earnest_money_deposit: optionalPositiveNumber,
  financing_contingency_days: optionalPositiveNumber,
  financing_contingency_date: z.string(),
  financing_contingency_mode: z.nativeEnum(FinancingContingencyMode),
  purchase_method: z.nativeEnum(PurchaseMethod),
  loan_type: z.nativeEnum(LoanType),
  special_stipulations: z.string().max(4_000, "Keep stipulations under 4,000 characters."),
  home_sale_contingency_enabled: z.boolean(),
  home_sale_contingency_address: z.string().max(300),
  kickout_clause_enabled: z.boolean(),
  kickout_clause_details: z.string().max(2_000),
  closing_address: z.string().max(300),
  closing_datetime: z.string(),
  closing_contact_name: z.string().max(120),
  closing_contact_phone: z.string().max(40),
  ...contractPromptFields,
}).superRefine(checkContractDateOrder).superRefine(checkInspectionWindow);

/** The under-contract prompt — the 17 terms an agent captures at contract.
 *  A strict subset of `dealSummarySchema`'s field set, so both forms can be
 *  fed by `getDealSummaryDefaults` and both land on the same sub-document. */
export const contractTermsPromptSchema = z.object({
  purchase_price: optionalPositiveNumber,
  earnest_money_deposit: optionalPositiveNumber,
  due_diligence_end_date: z.string(),
  closing_date: z.string(),
  financing_contingency_date: z.string(),
  home_sale_contingency_enabled: z.boolean(),
  special_stipulations: z.string().max(4_000, "Keep stipulations under 4,000 characters."),
  ...contractPromptFields,
}).superRefine(checkContractDateOrder).superRefine(checkInspectionWindow);

export const snapshotStepSchema = z.object({
  id: z.string(),
  label: z.string().trim().max(200, "Keep the step under 200 characters."),
  owner_role: z.nativeEnum(SnapshotOwner),
  due_date: z.string(),
  status: z.nativeEnum(NextStepStatus),
});

export const snapshotSchema = z.object({
  health: z.union([z.literal(""), z.nativeEnum(DealHealth)]),
  note: z.string().trim().max(600, "Keep the update under 600 characters."),
  auto_steps: z.boolean(),
  steps: z.array(snapshotStepSchema).max(3, "Add no more than three next steps."),
});

export type DelegatedInviteValues = z.infer<typeof delegatedInviteSchema>;
export type ScheduleTaskValues = z.infer<typeof scheduleTaskSchema>;
export type AskValues = z.infer<typeof askSchema>;
export type DealUpdateValues = z.infer<typeof dealUpdateSchema>;
export type ClientInviteValues = z.infer<typeof clientInviteSchema>;
export type CollaboratorInviteValues = z.infer<typeof collaboratorInviteSchema>;
export type TaskValues = z.infer<typeof taskSchema>;
export type DocumentValues = z.infer<typeof documentSchema>;
export type ParticipantValues = z.infer<typeof participantSchema>;
export type MicrositeValues = z.infer<typeof micrositeSchema>;
export type DealSummaryValues = z.infer<typeof dealSummarySchema>;
export type ContractTermsPromptValues = z.infer<typeof contractTermsPromptSchema>;
export type SnapshotStepValues = z.infer<typeof snapshotStepSchema>;
export type SnapshotValues = z.infer<typeof snapshotSchema>;


export const appointmentSchema = z.object({
  kind: z.enum(["showing", "meeting", "open_house", "broker_open"]),
  title: z.string().trim().min(1, "Give it a title.").max(140),
  date: z.string().min(1, "Pick a date."),
  start_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Pick a valid start time."),
  end_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Pick a valid end time."),
  timezone: z.string().min(1),
  location: z.string().trim().max(300),
  notes: z.string().trim().max(2000),
  attendee_user_ids: z.array(z.string()).max(50),
  guest_emails: z.string(),
}).superRefine((v, ctx) => {
  for (const field of ["start_time", "end_time"] as const) {
    try { localToIso(v.date, v[field], v.timezone); }
    catch (error) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: error instanceof Error ? error.message : "Pick a valid date and time." });
    }
  }
  if (v.end_time <= v.start_time) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["end_time"], message: "End after it starts." });
  try {
    const start = new Date(localToIso(v.date, v.start_time, v.timezone));
    const end = new Date(localToIso(v.date, v.end_time, v.timezone));
    if (end.getTime() - start.getTime() > 12 * 3600000) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["end_time"], message: "Up to 12 hours." });
  } catch { /* Field errors above explain invalid wall-clock times. */ }
  const guests = v.guest_emails.split(/[\s,;]+/).filter(Boolean);
  if (guests.length > 5) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["guest_emails"], message: "Up to 5 guests." });
  if (guests.some((g) => !z.string().email().safeParse(g).success)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["guest_emails"], message: "Check the guest email addresses." });
});
export type AppointmentValues = z.infer<typeof appointmentSchema>;
